import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, FileText } from "lucide-react";

function dataUrlToBlobUrl(dataUrl) {
  try {
    const [meta, b64] = dataUrl.split(",");
    const mime = (meta.match(/data:([^;]+)/) || [])[1] || "application/octet-stream";
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return URL.createObjectURL(new Blob([bytes], { type: mime }));
  } catch {
    return null;
  }
}

function openFile(url) {
  if (!url) return;
  if (url.startsWith("data:")) {
    const blobUrl = dataUrlToBlobUrl(url);
    if (blobUrl) {
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      // Revoke after a delay so the tab has time to load
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Detects file type and shows an inline viewer for PDF / image / unknown,
 * with a download button. `url` may be an http(s) URL or a data: URL.
 */
export default function FileViewer({ open, onClose, title, url, filename }) {
  const isPdf = !!url && (url.startsWith("data:application/pdf") || /\.pdf(\?|$)/i.test(url));
  const isImage =
    !!url &&
    (url.startsWith("data:image/") ||
      /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(url));

  const safeName = (filename || title || "kelta-document").replace(/[^a-z0-9._-]+/gi, "_");
  const finalName = isPdf
    ? safeName.endsWith(".pdf") ? safeName : `${safeName}.pdf`
    : isImage && !/\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(safeName)
    ? `${safeName}.jpg`
    : safeName;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-base leading-snug pr-4">{title}</DialogTitle>
            <div className="flex gap-2 flex-shrink-0">
              {url && (
                <>
                  <a href={url} download={finalName}>
                    <Button size="sm" className="bg-brand-blue text-white hover:bg-brand-blue/90" data-testid="file-download-btn">
                      <Download className="w-4 h-4 mr-1.5" /> Download
                    </Button>
                  </a>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => openFile(url)}
                    data-testid="file-open-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Open
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogHeader>
        <div className="bg-muted/40 max-h-[78vh] overflow-auto">
          {!url ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              No attachment provided for this document.
            </div>
          ) : isPdf ? (
            <iframe
              src={url}
              title={title}
              className="w-full h-[78vh] bg-white"
            />
          ) : isImage ? (
            <img src={url} alt={title} className="max-w-full max-h-[78vh] mx-auto block" />
          ) : (
            <div className="p-12 text-center">
              <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Preview not available for this file type. Use the download button.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
